import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, throwError, timeout, retry, of, delay } from 'rxjs';
import { environment } from '../../environments/environment';

export interface CourseInformation {
  id?: string;
  title?: string;
  description?: string;
  instructor?: string;
  duration?: number;
  price?: number;
  category?: string;
  level?: string;
  maxStudents?: number;
  currentStudents?: number;
  regisSeat?: number;
  startDate?: string;
  endDate?: string;
  status?: string;
  courseStatusId?: number;
  courseStatus?: {
    id: number;
    name: string;
    createdAt: string;
    updatedAt: string;
  };
  isActive?: boolean;
  certificationBySEC?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CourseFilters {
  searchTerm: string;
  category: string;
  level: string;
  status: string;
  instructor: string;
  certificationBySEC?: string; // '' | 'true' | 'false'
  dateRange?: string;          // '' | '3m' | '6m' | '1y' | '3y'
}

export interface CoursePaginatedResponse {
  courses: CourseInformation[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

@Injectable({
  providedIn: 'root'
})
export class CourseService {
  private readonly API_BASE = environment.apiUrl; // ใช้ environment config
  private readonly API_TIMEOUT = 30000; // 30 seconds
  private readonly MAX_RETRIES = 2;

  constructor(private http: HttpClient) {
    console.log('🌐 CourseService API URL:', this.API_BASE);
  }

  /**
   * Get all courses
   */
  getAllCourses(): Observable<CourseInformation[]> {
    console.log('🔄 CourseService: กำลังเรียกข้อมูลหลักสูตรจาก backend API...');
    console.log('🌐 API URL:', `${this.API_BASE}/courses`);
    
    return this.http.get<CourseInformation[]>(`${this.API_BASE}/courses`)
      .pipe(
        timeout(this.API_TIMEOUT),
        retry(this.MAX_RETRIES),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Get course by ID
   */
  getCourseById(id: string): Observable<CourseInformation> {
    console.log('🔄 CourseService: กำลังเรียกข้อมูลหลักสูตร ID จาก backend API:', id);
    console.log('🌐 API URL:', `${this.API_BASE}/courses/${id}`);
    
    return this.http.get<CourseInformation>(`${this.API_BASE}/courses/${id}`)
      .pipe(
        timeout(this.API_TIMEOUT),
        retry(this.MAX_RETRIES),
        catchError((error: HttpErrorResponse) => {
          console.error('❌ CourseService: ไม่สามารถเชื่อมต่อ course details API:', error.message);
          console.error('❌ Error status:', error.status, 'Error details:', error.error);
          
          // If API fails, return appropriate error message
          let errorMessage = 'ไม่สามารถโหลดข้อมูลหลักสูตรได้';
          
          switch (error.status) {
            case 0:
              errorMessage = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้';
              break;
            case 404:
              errorMessage = 'ไม่พบหลักสูตรที่ต้องการ';
              break;
            case 401:
              errorMessage = 'กรุณาเข้าสู่ระบบใหม่';
              break;
            case 403:
              errorMessage = 'คุณไม่มีสิทธิ์เข้าถึงข้อมูลนี้';
              break;
            case 500:
              errorMessage = 'เกิดข้อผิดพลาดภายในเซิร์ฟเวอร์';
              break;
          }
          
          return throwError(() => errorMessage);
        })
      );
  }

  /**
   * Create new course
   */
  createCourse(course: Partial<CourseInformation>): Observable<CourseInformation> {
    return this.http.post<CourseInformation>(`${this.API_BASE}/courses`, course)
      .pipe(
        timeout(this.API_TIMEOUT),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Update existing course
   */
  updateCourse(id: string, course: Partial<CourseInformation>): Observable<CourseInformation> {
    return this.http.put<CourseInformation>(`${this.API_BASE}/courses/${id}`, course)
      .pipe(
        timeout(this.API_TIMEOUT),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Delete course
   */
  deleteCourse(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE}/courses/${id}`)
      .pipe(
        timeout(this.API_TIMEOUT),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Get mock data when API is not available
   */
  private getMockCourses(): Observable<CourseInformation[]> {
    const mockCourses: CourseInformation[] = [
      {
        id: '1',
        title: 'หลักสูตรการประเมินราคาอสังหาริมทรัพย์ขั้นพื้นฐาน',
        description: 'เรียนรู้หลักการพื้นฐานในการประเมินราคาอสังหาริมทรัพย์',
        instructor: 'อาจารย์สมชาย วงศ์ใหญ่',
        duration: 40,
        price: 8500,
        category: 'Property Valuation',
        level: 'Beginner',
        maxStudents: 30,
        currentStudents: 25,
        startDate: '2024-02-01',
        endDate: '2024-02-28',
        status: 'Active',
        createdAt: '2024-01-15T08:00:00Z',
        updatedAt: '2024-01-20T10:30:00Z'
      },
      {
        id: '2',
        title: 'หลักสูตรการประเมินราคาเครื่องจักรและอุปกรณ์',
        description: 'การประเมินราคาเครื่องจักรและอุปกรณ์อุตสาหกรรม',
        instructor: 'อาจารย์วิไล เจริญสุข',
        duration: 32,
        price: 7200,
        category: 'Machinery Valuation',
        level: 'Intermediate',
        maxStudents: 25,
        currentStudents: 18,
        startDate: '2024-03-01',
        endDate: '2024-03-25',
        status: 'Open',
        createdAt: '2024-01-10T09:00:00Z',
        updatedAt: '2024-01-18T14:20:00Z'
      },
      {
        id: '3',
        title: 'หลักสูตรการประเมินธุรกิจขั้นสูง',
        description: 'การประเมินมูลค่าธุรกิจและการลงทุนขั้นสูง',
        instructor: 'อาจารย์ดร.ประยุทธ ชาญชัย',
        duration: 48,
        price: 12000,
        category: 'Business Valuation',
        level: 'Advanced',
        maxStudents: 20,
        currentStudents: 15,
        startDate: '2024-04-01',
        endDate: '2024-04-30',
        status: 'Open',
        createdAt: '2024-01-05T07:30:00Z',
        updatedAt: '2024-01-22T16:45:00Z'
      }
    ];

    return of(mockCourses).pipe(delay(500));
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ';

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `ข้อผิดพลาดจากไคลเอนต์: ${error.error.message}`;
    } else {
      // Server-side error
      switch (error.status) {
        case 0:
          errorMessage = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้';
          // Return mock data when server is not available
          return this.getMockCourses() as any;
        case 400:
          errorMessage = 'ข้อมูลที่ส่งมาไม่ถูกต้อง';
          break;
        case 401:
          errorMessage = 'กรุณาเข้าสู่ระบบใหม่';
          break;
        case 403:
          errorMessage = 'คุณไม่มีสิทธิ์เข้าถึงข้อมูลนี้';
          break;
        case 404:
          errorMessage = 'ไม่พบข้อมูลที่ต้องการ';
          break;
        case 500:
          errorMessage = 'เกิดข้อผิดพลาดภายในเซิร์ฟเวอร์';
          break;
        default:
          errorMessage = `เกิดข้อผิดพลาด: ${error.status} ${error.statusText}`;
      }
    }

    console.error('Course Service Error:', error);
    return throwError(() => errorMessage);
  }
} 